// ═══════════════════════════════════════════════════════════════
// SHARED API AUTH TOKEN
// Most edge function routes are tier-gated (requireTier middleware)
// and reject the anon key. AuthContext sets the user's session
// access token here on sign in / session resume; everything that
// hits the edge function should use bearerHeader() instead of
// hard-coding `Bearer ${publicAnonKey}`.
//
// Falls back to the anon key when no session is loaded yet (e.g.
// during the initial /health probe before sign-in completes), so
// public routes still work.
// ═══════════════════════════════════════════════════════════════

import { publicAnonKey } from './supabase/info';

let currentToken: string | null = null;

export function setApiAuthToken(token: string | null): void {
  currentToken = token;
}

export function getApiAuthToken(): string | null {
  return currentToken;
}

/** The full `Bearer xxx` value for an Authorization header. */
export function bearerHeader(): string {
  return `Bearer ${currentToken ?? publicAnonKey}`;
}

/** Convenience for `fetch(url, { headers: authHeaders() })`. */
export function authHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: bearerHeader(),
    ...(extra ?? {}),
  };
}

// ── Strict variants for tier-gated writes ──
// The anon-key fallback above is right for public reads, but on a requireTier(n)
// route it is a guaranteed 401 ("missing sub claim") — silently degrading turns an
// auth race into a failed write with a misleading error.

export function hasSessionToken(): boolean {
  return currentToken !== null;
}

/** Like bearerHeader() but throws instead of degrading to the anon key. */
export function strictBearerHeader(): string {
  if (!currentToken) {
    throw new Error('No signed-in session — this change needs a user token. Sign in again and retry.');
  }
  return `Bearer ${currentToken}`;
}

/**
 * fetch() for tier-gated writes: rejects up front when no session token is loaded,
 * so the failure flows through the caller's existing .catch (toast + rollback /
 * refetch) instead of firing a doomed anon-key request. Sets JSON + Authorization
 * headers; pass `headers` in `init` to extend or override.
 */
export function strictFetch(url: string, init: RequestInit = {}): Promise<Response> {
  if (!currentToken) {
    return Promise.reject(new Error('No signed-in session — sign in again and retry.'));
  }
  return fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentToken}`,
      ...((init.headers as Record<string, string> | undefined) ?? {}),
    },
  });
}
